import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "stores";

interface ProductionMetrics {
  dailyOutput: number;
  weeklyOutput: number;
  monthlyOutput: number;
  tableUtilization: number;
  activeHolds: number;
  scheduledPanels: number;
  completedToday: number;
  inProgress: number;
  qualityPassRate: number;
  averageProductionTime: number;
  bottlenecks: string[];
  resourceAllocation: {
    tables: { total: number; inUse: number };
    molds: { total: number; inUse: number };
    crews: { total: number; active: number };
  };
}

interface ProductionTrend {
  date: string;
  output: number;
  efficiency: number;
  holds: number;
}

export default class ProductionDashboardViewStore {
  isLoading = false;
  error: string | null = null;
  selectedDateRange: "today" | "week" | "month" = "today";
  selectedJobId: number | null = null;
  metrics: ProductionMetrics = {
    dailyOutput: 0,
    weeklyOutput: 0,
    monthlyOutput: 0,
    tableUtilization: 0,
    activeHolds: 0,
    scheduledPanels: 0,
    completedToday: 0,
    inProgress: 0,
    qualityPassRate: 0,
    averageProductionTime: 0,
    bottlenecks: [],
    resourceAllocation: {
      tables: { total: 0, inUse: 0 },
      molds: { total: 0, inUse: 0 },
      crews: { total: 0, active: 0 },
    },
  };
  productionTrends: ProductionTrend[] = [];
  recentActivities: any[] = [];
  upcomingSchedule: any[] = [];
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as any);
  }

  async loadDashboardData() {
    this.isLoading = true;
    this.error = null;

    try {
      await Promise.all([
        this.loadProductionMetrics(),
        this.loadActiveHolds(),
        this.loadScheduleData(),
        this.loadQualityMetrics(),
        this.loadResourceUtilization(),
      ]);
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load dashboard data";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  private async loadProductionMetrics() {
    const panelStore = (this.rootStore as any).panelStore;
    const productionOrdersStore = (this.rootStore as any).productionOrdersStore;

    if (panelStore) {
      await panelStore.find({
        query: {
          $limit: 500,
          createdAt: { $gte: this.getDateRangeStart() },
        },
      });

      const panels = panelStore.entities || [];

      runInAction(() => {
        // Calculate daily output
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        this.metrics.completedToday = panels.filter((panel: any) => {
          const completedDate = new Date(panel.completedAt || panel.updatedAt);
          completedDate.setHours(0, 0, 0, 0);
          return (
            panel.status === "completed" &&
            completedDate.getTime() === today.getTime()
          );
        }).length;

        // In progress panels
        this.metrics.inProgress = panels.filter(
          (panel: any) =>
            panel.status === "in_production" || panel.status === "casting"
        ).length;

        // Calculate weekly and monthly output
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);

        this.metrics.weeklyOutput = panels.filter((panel: any) => {
          const date = new Date(panel.completedAt || panel.updatedAt);
          return panel.status === "completed" && date >= weekAgo;
        }).length;

        this.metrics.monthlyOutput = panels.filter((panel: any) => {
          const date = new Date(panel.completedAt || panel.updatedAt);
          return panel.status === "completed" && date >= monthAgo;
        }).length;

        this.metrics.dailyOutput = this.metrics.completedToday;
      });
    }

    if (productionOrdersStore) {
      await productionOrdersStore.find({ query: { $limit: 100 } });

      runInAction(() => {
        const orders = productionOrdersStore.entities || [];
        const activeOrders = orders.filter(
          (order: any) =>
            order.status === "in_progress" || order.status === "processing"
        );

        // Recent activities from production orders
        this.recentActivities = orders.slice(0, 10).map((order: any) => ({
          id: order.id,
          type: "production_order",
          description: `Order #${order.id} - ${order.status}`,
          timestamp: order.updatedAt || order.createdAt,
          status: order.status,
        }));
      });
    }
  }

  private async loadActiveHolds() {
    const holdStore = (this.rootStore as any).holdStore;

    if (holdStore) {
      await holdStore.find({
        query: {
          status: "active",
          $limit: 100,
        },
      });

      runInAction(() => {
        const holds = holdStore.entities || [];
        this.metrics.activeHolds = holds.filter(
          (hold: any) => hold.status === "active" || !hold.status
        ).length;

        // Identify bottlenecks from holds
        const holdReasons = holds.reduce((acc: any, hold: any) => {
          const reason = hold.reason || hold.type || "Unknown";
          acc[reason] = (acc[reason] || 0) + 1;
          return acc;
        }, {});

        this.metrics.bottlenecks = Object.entries(holdReasons)
          .sort((a: any, b: any) => b[1] - a[1])
          .slice(0, 3)
          .map(([reason]: any) => reason);
      });
    }
  }

  private async loadScheduleData() {
    const scheduleStore = (this.rootStore as any).schedulingDayStore;
    const jobScheduleStore = (this.rootStore as any).jobScheduleStore;

    if (scheduleStore) {
      await scheduleStore.find({
        query: {
          date: { $gte: new Date().toISOString() },
          $limit: 50,
        },
      });

      runInAction(() => {
        const schedules = scheduleStore.entities || [];
        this.metrics.scheduledPanels = schedules.reduce(
          (sum: number, schedule: any) => {
            return sum + (schedule.panelCount || 0);
          },
          0
        );
      });
    }

    if (jobScheduleStore) {
      await jobScheduleStore.find({
        query: {
          date: { $gte: new Date().toISOString() },
          $limit: 20,
        },
      });

      runInAction(() => {
        const schedules = jobScheduleStore.entities || [];
        this.upcomingSchedule = schedules.slice(0, 5).map((schedule: any) => ({
          id: schedule.id,
          jobNumber: schedule.jobNumber,
          date: schedule.date,
          panels: schedule.panels || [],
          status: schedule.status,
        }));
      });
    }
  }

  private async loadQualityMetrics() {
    // Load quality control data if available
    const qualityStore = (this.rootStore as any).qualityControlStore;

    if (qualityStore) {
      try {
        await qualityStore.find({
          query: {
            $limit: 100,
            createdAt: { $gte: this.getDateRangeStart() },
          },
        });

        runInAction(() => {
          const records = qualityStore.entities || [];
          const passedRecords = records.filter(
            (record: any) =>
              record.status === "passed" || record.result === "pass"
          ).length;

          if (records.length > 0) {
            this.metrics.qualityPassRate =
              (passedRecords / records.length) * 100;
          } else {
            this.metrics.qualityPassRate = 100; // Default to 100% if no records
          }
        });
      } catch (error) {
        console.warn("Quality control store not available:", error);
      }
    }
  }

  private async loadResourceUtilization() {
    const castingMapStore = (this.rootStore as any).castingMapLayoutStore;
    const moldAssignmentStore = (this.rootStore as any)
      .moldEngineerAssignmentsStore;
    const engineersStore = (this.rootStore as any).engineersStore;

    // Load casting table utilization
    if (castingMapStore) {
      try {
        await castingMapStore.find({ query: { $limit: 100 } });

        runInAction(() => {
          const layouts = castingMapStore.entities || [];
          const totalTables = layouts.length;
          const inUseTables = layouts.filter(
            (layout: any) => layout.status === "occupied" || layout.panelId
          ).length;

          this.metrics.resourceAllocation.tables = {
            total: totalTables || 20, // Default to 20 tables
            inUse: inUseTables,
          };

          this.metrics.tableUtilization =
            totalTables > 0 ? (inUseTables / totalTables) * 100 : 0;
        });
      } catch (error) {
        console.warn("Production Floor Map store not available:", error);
      }
    }

    // Load mold assignments
    if (moldAssignmentStore) {
      try {
        await moldAssignmentStore.find({ query: { $limit: 100 } });

        runInAction(() => {
          const assignments = moldAssignmentStore.entities || [];
          const totalMolds = assignments.length || 50; // Default estimate
          const inUseMolds = assignments.filter(
            (assignment: any) =>
              assignment.status === "active" || assignment.assigned
          ).length;

          this.metrics.resourceAllocation.molds = {
            total: totalMolds,
            inUse: inUseMolds,
          };
        });
      } catch (error) {
        console.warn("Mold assignment store not available:", error);
      }
    }

    // Load engineer/crew utilization
    if (engineersStore) {
      try {
        await engineersStore.find({ query: { $limit: 100 } });

        runInAction(() => {
          const engineers = engineersStore.entities || [];
          const totalCrews = engineers.length;
          const activeCrews = engineers.filter(
            (engineer: any) => engineer.status === "busy" || engineer.assigned
          ).length;

          this.metrics.resourceAllocation.crews = {
            total: totalCrews || 10, // Default estimate
            active: activeCrews,
          };
        });
      } catch (error) {
        console.warn("Engineers store not available:", error);
      }
    }

    // Calculate production trends
    this.calculateProductionTrends();
  }

  private calculateProductionTrends() {
    const trends: ProductionTrend[] = [];
    const today = new Date();

    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(today.getDate() - i);

      trends.push({
        date: date.toISOString().split("T")[0],
        output: Math.floor(Math.random() * 50) + 20, // Placeholder - should calculate from real data
        efficiency: Math.floor(Math.random() * 30) + 70, // Placeholder
        holds: Math.floor(Math.random() * 5), // Placeholder
      });
    }

    runInAction(() => {
      this.productionTrends = trends;
    });
  }

  private getDateRangeStart(): string {
    const date = new Date();

    switch (this.selectedDateRange) {
      case "today":
        date.setHours(0, 0, 0, 0);
        break;
      case "week":
        date.setDate(date.getDate() - 7);
        break;
      case "month":
        date.setMonth(date.getMonth() - 1);
        break;
    }

    return date.toISOString();
  }

  setDateRange(range: "today" | "week" | "month") {
    this.selectedDateRange = range;
    this.loadDashboardData();
  }

  setSelectedJob(jobId: number | null) {
    this.selectedJobId = jobId;
    this.loadDashboardData();
  }

  async refreshDashboard() {
    await this.loadDashboardData();
  }

  clearError() {
    this.error = null;
  }

  get jobs() {
    return ((this.rootStore as any).jobStore as any)?.entities || [];
  }

  get productionEfficiency() {
    const { tables, molds, crews } = this.metrics.resourceAllocation;

    if (tables.total === 0 && molds.total === 0 && crews.total === 0) {
      return 0;
    }

    const tableEfficiency = tables.total > 0 ? tables.inUse / tables.total : 0;
    const moldEfficiency = molds.total > 0 ? molds.inUse / molds.total : 0;
    const crewEfficiency = crews.total > 0 ? crews.active / crews.total : 0;

    return ((tableEfficiency + moldEfficiency + crewEfficiency) / 3) * 100;
  }

  get scheduleAdherence() {
    if (this.metrics.scheduledPanels === 0) return 100;

    const completionRate =
      (this.metrics.completedToday / this.metrics.scheduledPanels) * 100;
    return Math.min(completionRate, 100); // Cap at 100%
  }

  reset() {
    this.isLoading = false;
    this.error = null;
    this.selectedDateRange = "today";
    this.selectedJobId = null;
    this.productionTrends = [];
    this.recentActivities = [];
    this.upcomingSchedule = [];
  }
}
